<template>
  <nav v-if="$store.state.nav.desktopNavigation" class="desktop-navigation">
    <div class="skip-navigation" ref="skipNavigation">
      <a
        class="skip-navigation__link"
        href="#site-content"
        tabindex="1"
        @focus="toggleSkipNavigation"
        @blur="toggleSkipNavigation(true)"
        >{{ $t('navigation.skip_navigation') }}
      </a>
    </div>

    <transition name="fade-nav-overlay">
      <div
        v-show="overlayIsShown"
        class="desktop-navigation__overlay-background"
        @click=";[setAdditionalNav(''), setOverlayState]"
      ></div>
    </transition>
    <div
      class="main-navigation"
      :style="{ paddingRight: scrollbarEqualizerMargin }"
      @click="setAdditionalNav('')"
    >
      <!-- Logo -->
      <NavLogo :homepageUrl="homepageUrl"></NavLogo>
      <!-- Main links -->
      <div class="main-links" role="menubar" :aria-label="$t('navigation.main_links')">
        <a
          v-for="(link, index) in $store.state.nav.topics"
          ref="mainLink"
          :key="index"
          :href="link.href"
          class="main-links__link"
          :aria-haspopup="link.blocks.length > 0 ? 'true' : 'false'"
          :aria-exanded="subNavIsOpen && currentTopic === index ? 'true' : 'false'"
          @mouseenter="onMainLinkMouseenter(link, index)"
          @mouseleave="onMainLinkLeave"
          @focus="link.blocks.length ? openSubnav(index, link.href) : closeSubnav()"
          @click.prevent="onMainLinkClick(link.href)"
          @keydown="onMainLinkKeydown($event, index, link)"
        >
          <div
            class="main-links__link-copy"
            :class="currentTopic === index && subNavIsOpen ? 'is-active' : ''"
          >
            {{ link.title }}
          </div>
        </a>
      </div>
      <!-- Additional main links -->
      <AdditionalMainLinks
        :links="additionalMainLinks"
        @closeSubnav="closeSubnav"
        @registerSubnavKeyListener="registerSubnavKeyListener"
      ></AdditionalMainLinks>
    </div>
    <!-- Subnavigation -->
    <div
      ref="subnav"
      class="sub-navigation"
      @mouseenter="openSubnav(currentTopic, undefined, false)"
      @mouseleave="closeSubnav()"
      @click.prevent="handleSubnavClick($event)"
    >
      <div
        ref="subnavcontent"
        class="sub-navigation__main-content"
        :class="{ 'is-transitioning': subNavIsTransitioning }"
        @animationend="subNavIsTransitioning = false"
      >
        <div
          class="sub-navigation__text-content"
          :class="{ 'wide-desktop-viewport': wideDesktopViewport }"
        >
          <div class="sub-navigation__block-container">
            <div
              v-for="(block, blockIndex) in subNavBlocks"
              :key="blockIndex"
              class="sub-navigation__block"
              @mouseenter="setCurrentBlock(blockIndex)"
            >
              <a
                ref="blocktitle"
                :href="block.href"
                class="sub-navigation__block-title"
                :tabindex="!block.href ? -1 : null"
              >
                <div class="sub-navigation__block-title-copy">{{ block.title }}</div>
              </a>
              <div
                v-for="(section, sectionIndex) in block.sections"
                :key="sectionIndex"
                class="sub-navigation__section"
                :class="{
                  'sub-navigation__section--no-sublinks': !section.links.length,
                }"
              >
                <a
                  class="sub-navigation__section-title"
                  :class="{
                    'sub-navigation__section-title--no-href': !section.href,
                    'sub-navigation__section-title--no-sublinks': !section.links.length,
                    'sub-navigation__section-title--last-column':
                      blockIndex === subNavBlocks.length - 1,
                  }"
                  :href="section.href"
                >
                  {{ section.title }}
                </a>
                <div class="sub-navigation__links" v-if="section.links.length">
                  <a
                    v-for="(link, index) in section.links"
                    :href="link.href"
                    :key="index"
                    class="sub-navigation__link"
                    >{{ link.text }}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="sub-navigation__delivery-notice" :style="deliveryNoticeStyle">
            <div class="sub-navigation__delivery-notice-inner">
              <div class="sub-navigation__delivery-separator"></div>
              <div class="sub-navigation__delivery-copy-wrap">
                <span class="sub-navigation__delivery-copy" v-html="formattedDeliveryNotice"></span>
              </div>
            </div>
          </div>
        </div>

        <ImageContentful
          v-if="isTeaserShowing"
          tag="a"
          :href="topic.teaser.ctaLink"
          :src="topic.teaser.imageUrl"
          containerSelector=".sub-navigation__teaser"
          :isBackgroundImage="true"
          :options="{ imageCompression: 61, setHeight: false, retinaSupportFactor: 0.2 }"
          class="sub-navigation__teaser"
          :class="{ 'light-theme': !topic.teaser.theme }"
          @click="trackTeaserClick"
        >
          <div class="sub-navigation__teaser-content">
            <p class="sub-navigation__teaser-text">{{ topic.teaser.text }}</p>
            <a :href="topic.teaser.ctaLink" class="sub-navigation__teaser-link">
              {{ topic.teaser.ctaText }}
            </a>
          </div>
        </ImageContentful>
      </div>
    </div>
    <!-- Additional navigation -->
    <div class="additional-nav">
      <AccountNav v-if="shouldRenderAccountNav" />
      <Search />
      <MiniCart />
    </div>
  </nav>
</template>

<script>
import desktopNavigation from './DesktopNav'
export default desktopNavigation
</script>
